import _ from 'lodash';
import React from 'react';
import {loadingTextsFor} from './utils';
import {useScoringRun} from '../../../context/ScoringRunContext';
import {useScoringSignalResolver} from '../../../hooks/scoringSignal';
import {ScoringSignal} from '../../../shared/signals';
import {filterNull} from '../../../shared/util';
import {useAccountQueryParams} from '../AccountGrid.controlstate';
import {useSignalCounts} from '../useSignalCounts';
import {Box, Button, CloseButton, Flex, Text} from '@chakra-ui/react';
import {
  FilterSelectHeight,
  FilterSelect,
  FilterOperatorSelectWidth,
} from './FilterSelect';
import {AddLarge} from '@carbon/icons-react';
import {useMultiGroupSelect} from '../../useMultiGroupSelect';

const ScoringSignalFilterLoadingTexts = loadingTextsFor('signals');
const filterSelectionOptions = {
  include: {label: 'include any of', value: 'include any of'},
};

export const ScoringSignalsFilterControl = ({
  selectedSignals,
  setSelectedSignals,
  onRemove,
}: {
  selectedSignals: ScoringSignal[][];
  setSelectedSignals: (signals: ScoringSignal[][]) => void;
  onRemove?: () => void;
}) => {
  const scoringRun = useScoringRun();
  const queryParams = useAccountQueryParams(scoringRun._id);
  const signalCounts = useSignalCounts({
    queryParams,
    enabled: true,
  });
  const scoringSignalResolver = useScoringSignalResolver();

  let signalOptions: Array<{value: ScoringSignal; label: string}> = [];
  if (signalCounts.status === 'success') {
    signalOptions = filterNull(
      _.map(signalCounts.data, (count, signal) => {
        const signalDetails = scoringSignalResolver(signal);
        if (!signalDetails) {
          return null;
        }

        return {
          value: signalDetails.id,
          label: count
            ? `${signalDetails.label} (${count})`
            : signalDetails.label,
        };
      })
    );
  }

  const selectedSignalOptions = React.useMemo(
    () =>
      selectedSignals.map((signalGroup) =>
        filterNull(
          signalGroup.map((signal) => {
            const signalDetails = scoringSignalResolver(signal);
            if (!signalDetails) {
              return null;
            }

            return {
              value: signalDetails.id,
              label: signalDetails.label,
            };
          })
        )
      ),
    [scoringSignalResolver, selectedSignals]
  );

  const {
    availableOptions,
    onOptionGroupChanged,
    onRemoveOptionGroup,
    onAddOptionGroup,
    canAddGroup,
  } = useMultiGroupSelect(
    signalOptions,
    selectedSignalOptions,
    setSelectedSignals,
    3
  );

  const handleRemove = (index: number) => {
    if (selectedSignalOptions.length > 1) {
      onRemoveOptionGroup(index);
    } else {
      onRemove?.();
    }
  };

  return (
    <Flex gap="2" flex={2} direction={'column'}>
      {selectedSignalOptions.map((signalGroup, index) => (
        <Flex gap="2" flex={2} key={index}>
          {index === 0 ? (
            <FilterSelect
              variant="operator"
              options={Object.values(filterSelectionOptions)}
              value={filterSelectionOptions.include}
            />
          ) : (
            <Flex
              alignItems="center"
              justifyContent="flex-end"
              width={FilterOperatorSelectWidth}
              height={FilterSelectHeight}
            >
              <Text color={'kgray.300'}>and any of</Text>
            </Flex>
          )}
          <Flex gap={2} flex={2} key={index}>
            <Box flex={1} gap={2} display={'flex'} flexDirection={'column'}>
              <FilterSelect
                isMulti
                options={availableOptions}
                onChange={(values) => {
                  onOptionGroupChanged(values, index);
                }}
                value={signalGroup}
                noOptionsMessage={() =>
                  ScoringSignalFilterLoadingTexts[signalCounts.status]
                }
              />
              {index === selectedSignalOptions.length - 1 && canAddGroup && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onAddOptionGroup}
                  leftIcon={<AddLarge />}
                >
                  Add signal group
                </Button>
              )}
            </Box>
            <Flex height={FilterSelectHeight} alignItems="center">
              <CloseButton onClick={() => handleRemove(index)} />
            </Flex>
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
};
